import { useCallback } from 'react';
import {
  PublisherReviewStatus,
  ScannerErrorType,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';

import { log } from '../../loggers/Logger';
import { ScannerFindingsSummary } from '../../services/ScannerFindings';
import { isCommentResolved } from '../v2-common/utils/commentStateUtils';
import { usePublisherReviewService } from '../../contexts/PublisherReviewServiceContext';
import { useAuthorPanelSharedState } from './AuthorStatePanelViewModel';
import { ContentValidationIssue, useSaveContent } from '../v2-hooks/useSaveContent';

export type AuthorReviewSubState = 'inProcess' | 'rejected';

export interface PublisherReviewStageViewModel {
  readonly stage: 'publisherReview';
  readonly isBusy: boolean;
  readonly isSaveLoading: boolean;
  readonly isResubmitLoading: boolean;
  readonly isScanning: boolean;
  readonly isLoadingFindings: boolean;
  readonly scannerFindings: readonly ScannerFindingsSummary[];
  readonly subState: AuthorReviewSubState;
  /** Outstanding items the author must clear, in display order. */
  readonly requirementsToProceed: readonly string[];
  readonly scanWarningMessage: string | undefined;
  readonly errorMessage: string | undefined;
  readonly validationIssues: readonly ContentValidationIssue[] | undefined;
  readonly dismissError: () => void;
  readonly saveAndClose: () => void;
  readonly resubmitForReview: () => void;
}

export function usePublisherReviewStageViewModel(): PublisherReviewStageViewModel {
  const base = useAuthorPanelSharedState(WorkflowState.PUBLISHER_REVIEW);
  const {
    setBusyAction,
    setErrorMessage,
    setValidationIssues,
    dismissError,
    navigateToViewer,
    contentId,
    workflow,
  } = base;
  const publisherReviewService = usePublisherReviewService();
  const { save } = useSaveContent();

  const resubmitForReview = useCallback(async () => {
    if (!workflow || !contentId) {
      log.warn({ contentId }, 'Cannot resubmit for review: content not loaded');
      return;
    }
    setBusyAction('resubmit');
    dismissError();

    // Persist any unsaved edits so the bar raiser reviews the latest content.
    const saveResult = await save();
    if (!saveResult.ok) {
      setErrorMessage(saveResult.errorMessage);
      setValidationIssues(saveResult.validationIssues);
      setBusyAction(null);
      return;
    }

    try {
      await publisherReviewService.resubmitForReview(contentId, workflow.draftWorkflowId);
      navigateToViewer();
    } catch (err) {
      log.error({ err, contentId }, 'Failed to resubmit for publisher review');
      setErrorMessage('Failed to resubmit for publisher review. Please try again.');
    } finally {
      setBusyAction(null);
    }
  }, [
    setBusyAction,
    setErrorMessage,
    setValidationIssues,
    dismissError,
    navigateToViewer,
    contentId,
    workflow,
    publisherReviewService,
    save,
  ]);

  const reviewStatus = base.workflow?.publisherReviewInfo?.reviewStatus;
  const subState: AuthorReviewSubState =
    reviewStatus === PublisherReviewStatus.REJECTED ? 'rejected' : 'inProcess';

  const unresolvedCommentCount =
    base.scanResults?.scanComments?.filter((c) => !isCommentResolved(c.state)).length ?? 0;

  // The requirements differ by sub-state; a rejected review only needs the comment
  // step when comments are still open.
  const requirementsToProceed: readonly string[] =
    subState === 'rejected'
      ? [
          'Address bar raiser feedback',
          ...(unresolvedCommentCount > 0 ? ['Resolve all comments'] : []),
          'Resubmit for review',
        ]
      : ['Wait for bar raiser to complete review'];

  const scanWarningMessage = base.openedEdits?.errors.some(
    (error) => error.errorType === ScannerErrorType.GUARDRAIL_INTERVENED,
  )
    ? 'The quality check was stopped because some content triggered safety controls. Review and remove any potentially harmful or inappropriate material, then rescan.'
    : undefined;

  return {
    stage: 'publisherReview',
    isBusy: base.isBusy,
    isSaveLoading: base.isSaveLoading,
    isResubmitLoading: base.busyAction === 'resubmit',
    isScanning: base.isScanning,
    isLoadingFindings: base.isLoadingFindings,
    scannerFindings: base.scannerFindings,
    subState,
    requirementsToProceed,
    scanWarningMessage,
    errorMessage: base.errorMessage,
    validationIssues: base.validationIssues,
    dismissError,
    saveAndClose: base.saveAndClose,
    resubmitForReview,
  };
}
